import {
  OmsExportPreset,
  OmsPresetScope,
  OmsRecordFilter,
} from "../../lusca-search-api/generated-src";
import { OmsConfiguration, presetScopeLabel } from "./omsPresetEditing";
import { submittedCondition } from "./omsFilterEditing";

export type PresetChangeValue = {
  text: string;
  filter?: OmsRecordFilter;
  values?: string[];
  kind?: "columns" | "filters";
};
export type PresetChange = {
  id: string;
  label: string;
  before: PresetChangeValue;
  after: PresetChangeValue;
};
export type PresetChanges = { summaries: string[]; details: PresetChange[] };

function signature(filter: OmsRecordFilter) {
  return JSON.stringify({
    column: filter.column,
    operator: filter.operator,
    ...(filter.value !== undefined ? { value: filter.value } : {}),
    ...(filter.values ? { values: [...filter.values].sort() } : {}),
  });
}

export default function presetChanges(
  saved: OmsExportPreset,
  draft: OmsConfiguration,
  name: string,
  scope: OmsPresetScope
): PresetChanges {
  const summaries: string[] = [];
  const details: PresetChange[] = [];
  const add = (
    id: string,
    label: string,
    before: PresetChangeValue,
    after: PresetChangeValue
  ) => details.push({ id, label, before, after });
  if (saved.name !== name.trim().normalize("NFC")) {
    summaries.push("Name changed");
    add("name", "Name", { text: saved.name }, { text: name.trim() });
  }
  if (saved.scope !== scope) {
    summaries.push("Saved settings changed");
    add(
      "scope",
      "Saved settings",
      { text: presetScopeLabel[saved.scope] },
      { text: presetScopeLabel[scope] }
    );
    if (scope === "columns" && saved.scope !== "columns")
      summaries.push("Filters removed from preset");
    if (scope === "filters" && saved.scope !== "filters")
      summaries.push("Columns removed from preset");
  }
  if (scope !== "columns") {
    const beforeRows = saved.rows === "all" ? "All rows" : "Filtered rows";
    const afterRows = draft.mode === "full" ? "All rows" : "Filtered rows";
    if (saved.scope === "columns" || beforeRows !== afterRows) {
      summaries.push("Rows changed");
      add(
        "rows",
        "Rows to export",
        { text: saved.scope === "columns" ? "Not saved" : beforeRows },
        { text: afterRows }
      );
    }
    const before = saved.scope === "columns" ? [] : saved.filters || [];
    const after =
      draft.mode === "filtered"
        ? draft.criteria
            .filter((filter) => filter.column)
            .map(submittedCondition)
        : [];
    const remaining = after.map((filter, index) => ({ filter, index }));
    const removed = before.filter((filter) => {
      const match = remaining.findIndex(
        (candidate) => signature(filter) === signature(candidate.filter)
      );
      if (match < 0) return true;
      remaining.splice(match, 1);
      return false;
    });
    let updated = 0;
    let deleted = 0;
    removed.forEach((filter, index) => {
      const match = remaining.findIndex(
        (candidate) => candidate.filter.column === filter.column
      );
      const next = match < 0 ? undefined : remaining.splice(match, 1)[0].filter;
      if (next) updated += 1;
      else deleted += 1;
      add(
        `filter-before-${index}`,
        filter.column,
        { text: "", filter },
        next ? { text: "", filter: next } : { text: "Removed" }
      );
    });
    remaining.forEach(({ filter, index }) =>
      add(
        `filter-after-${index}`,
        filter.column,
        { text: "Not saved" },
        { text: "", filter }
      )
    );
    const counts = [
      updated ? `${updated} changed` : "",
      remaining.length ? `${remaining.length} added` : "",
      deleted ? `${deleted} removed` : "",
    ].filter(Boolean);
    if (counts.length) summaries.push(`Filters: ${counts.join(", ")}`);
    else if (
      JSON.stringify(before.map(signature)) !==
      JSON.stringify(after.map(signature))
    ) {
      summaries.push("Filter order changed");
      add(
        "filter-order",
        "Filter order",
        {
          text: "",
          values: before.map((filter) => filter.column),
          kind: "filters",
        },
        {
          text: "",
          values: after.map((filter) => filter.column),
          kind: "filters",
        }
      );
    }
    const beforeOperation = saved.operation || "and";
    if (
      draft.mode === "filtered" &&
      (beforeOperation !== draft.operation || saved.scope === "columns")
    ) {
      const label = (operation: "and" | "or") =>
        operation === "or" ? "Any condition (OR)" : "All conditions (AND)";
      summaries.push("Matching rule changed");
      add(
        "operation",
        "Matching rule",
        {
          text:
            saved.scope === "columns" ? "Not saved" : label(beforeOperation),
        },
        { text: label(draft.operation) }
      );
    }
  }
  if (scope !== "filters") {
    const before = saved.scope === "filters" ? undefined : saved.columns;
    const after = draft.columns;
    const label = (strategy?: string, names: string[] = []) =>
      strategy === "all" ? "All columns" : `${names.length} selected columns`;
    if (!before || before.strategy !== after.strategy) {
      summaries.push(
        `Columns: ${
          before ? label(before.strategy, before.names) : "Not saved"
        } → ${label(after.strategy, after.names)}`
      );
      add(
        "column-selection",
        "Output columns",
        {
          text: before ? label(before.strategy, before.names) : "Not saved",
          ...(before?.strategy === "selected" ? { values: before.names } : {}),
        },
        {
          text: label(after.strategy, after.names),
          ...(after.strategy === "selected" ? { values: after.names } : {}),
        }
      );
    } else if (after.strategy === "selected") {
      const previous = before.names || [];
      const added = after.names.filter((column) => !previous.includes(column));
      const removed = previous.filter(
        (column) => !after.names.includes(column)
      );
      const reordered =
        JSON.stringify(
          previous.filter((column) => after.names.includes(column))
        ) !==
        JSON.stringify(
          after.names.filter((column) => previous.includes(column))
        );
      const changes = [
        added.length ? `${added.length} added` : "",
        removed.length ? `${removed.length} removed` : "",
        reordered ? "order changed" : "",
      ].filter(Boolean);
      if (changes.length) summaries.push(`Columns: ${changes.join(", ")}`);
      if (added.length)
        add(
          "columns-added",
          "Added columns",
          { text: "Not selected" },
          { text: "", values: added }
        );
      if (removed.length)
        add(
          "columns-removed",
          "Removed columns",
          { text: "", values: removed },
          { text: "Not selected" }
        );
      if (reordered)
        add(
          "columns-order",
          "Column order",
          { text: "", values: previous },
          { text: "", values: after.names }
        );
    }
  }
  return { summaries, details };
}
